import { useState, useCallback, useEffect } from 'react';
import { IStorageService, storageService } from '../../QnAConfigModal/services/StorageService';
import { ChatMessage } from '../types';

export interface UseMessageManagerReturn {
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  updateMessage: (messageId: string, content: string) => void;
  clearMessages: () => void;
  getMessagesForAI: () => Array<{ role: 'user' | 'assistant'; content: string }>;
}

export function useMessageManager(
  maxContextMessages = 20,
  storageServiceInstance: IStorageService = storageService,
): UseMessageManagerReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  useEffect(() => {
    const loadInitialMessages = async () => {
      const loadedMessages = await storageServiceInstance.loadMessages();
      setMessages(loadedMessages);
    };

    loadInitialMessages();
  }, [storageServiceInstance]);

  useEffect(() => {
    if (messages.length > 0) {
      storageServiceInstance.saveMessages(messages);
    }
  }, [messages, storageServiceInstance]);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const updateMessage = useCallback((messageId: string, content: string) => {
    setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, content } : msg)));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    storageServiceInstance.clearMessages();
  }, [storageServiceInstance]);

  const getMessagesForAI = useCallback(() => {
    const nonSystemMessages = messages.filter((msg) => msg.type !== 'system');
    const recentMessages = nonSystemMessages.slice(-maxContextMessages);

    return recentMessages.map((msg) => ({
      role: msg.type === 'user' ? 'user' as const : 'assistant' as const,
      content: msg.content,
    }));
  }, [messages, maxContextMessages]);

  return {
    messages,
    addMessage,
    updateMessage,
    clearMessages,
    getMessagesForAI,
  };
}
